
import { agencyMapActions, agencyMapState } from "@/store/modules/agency";
import { userMapActions, userMapGetters } from "@/store/modules/user";
import { IAgency } from "@/store/modules/agency/types";
import { cloneDeep, sortBy, uniqBy } from "lodash";
import Vue from "vue";
import { getAgenciesQueryFilter } from "@/helpers";
interface ISelectAgencies {
  agencies: IAgency[];
}
export default Vue.extend({
  name: "search-select-agencies",
  props: {
    value: {
      type: [Number, String, Array, Boolean],
      default: ""
    },
    label: {
      type: String,
      default: "",
      required: false
    },
    preFetch: {
      type: Boolean,
      required: false,
      default: false
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    placeholder: {
      type: String,
      required: false,
      default: "Type to search agencies"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    description: {
      type: String,
      required: false,
      default: ""
    },
    descriptionHTML: {
      type: String,
      required: false,
      default: ""
    },
    showOnlyAgencyCodes: {
      type: Boolean,
      required: false
    },
    useAgencyCodeAsValue: {
      type: Boolean,
      required: false
    },
    // Resolve saved values from the editing EZLynx user's ezlynxAgencies on
    // the server instead of sending every id to GET /agencies.
    resolveFromEzlynxUser: {
      type: Boolean,
      required: false
    }
  },
  data(): ISelectAgencies {
    return {
      agencies: []
    };
  },
  mounted() {
    if (this.preFetch) {
      this.remoteMethod("");
    }
  },
  watch: {
    value: {
      handler() {
        if (this.hasUnresolvedValues) {
          this.getAgenciesWithValue();
        }
      },
      immediate: true
    }
  },
  methods: {
    ...agencyMapActions(["getAgencies"]),
    ...userMapActions(["getEzlynxUserAgencies"]),
    getAgencyField(fieldName: string) {
      const { value } = this as any;
      if (typeof value === "string") {
        return value ? [value] : [];
      }
      const values: string[] = [];
      if (Array.isArray(value)) {
        for (const agency of value) {
          if (typeof agency === "string") {
            if (agency) {
              values.push(agency);
            }
          } else if (agency && agency[fieldName]) {
            values.push(agency[fieldName]);
          }
        }
      }
      return values;
    },
    async getAgenciesWithValue() {
      try {
        if (
          this.resolveFromEzlynxUser &&
          !this.useAgencyCodeAsValue &&
          this.getEditingUser &&
          this.getEditingUser._id
        ) {
          const ezlynxAgencies: IAgency[] = await this.getEzlynxUserAgencies(
            this.getEditingUser._id
          );
          this.agencies = uniqBy([...this.agencies, ...ezlynxAgencies], "_id");
          return;
        }
        let params = {};
        if (this.useAgencyCodeAsValue) {
          const agencyCodes = this.getAgencyField("agencyCode");
          if (!agencyCodes.length) {
            return;
          }
          params = {
            agencyCode__in: agencyCodes
          };
        } else {
          const agencyIds = this.getAgencyField("_id");
          if (!agencyIds.length) {
            return;
          }
          params = {
            _id__in: agencyIds
          };
        }

        const agency: any = await this.getAgencies({
          query: {
            ...params,
            // Resolve the already-saved value regardless of the agency's
            // current status, otherwise editing a user whose agency is not
            // "approved" renders an empty label for a value that does exist.
            // Don't filter by allowEzlynxRating either: saved ezlynx agencies
            // without the flag would not resolve, and the select would drop
            // them from the selection.
            ...getAgenciesQueryFilter(this.$getCurrentUser)
          },
          merge: true
        });
        this.agencies = uniqBy([...this.agencies, ...agency], "_id");
      } catch (error) {
        this.$appNotifyError("Error fetching agencies");
        this.$bugSnagClient.notify(error);
      }
    },
    async remoteMethod(searchTerm: string) {
      try {
        if (!searchTerm || (searchTerm && searchTerm.length < 4)) {
          return;
        }

        const filter = getAgenciesQueryFilter(this.$getCurrentUser);
        const searchResults: IAgency[] = await this.getAgencies({
          query: {
            __query: searchTerm,
            ...filter
          },
          merge: true
        });
        // Merge rather than replace: dropping previously loaded agencies
        // removes selected values from the options, and the select then
        // clears them from the selection.
        this.agencies = uniqBy([...this.agencies, ...searchResults], "_id");
      } catch (error) {
        this.$appNotifyError("Error fetching agencies");
        this.$bugSnagClient.notify(error);
      }
    }
  },
  computed: {
    ...agencyMapState(["makingApiRequest", "editing"]),
    ...userMapGetters(["getEditingUser"]),
    // INFSelectField maps `selected` onto its internal model in a watcher that
    // observes `selected` only, dropping any value missing from `options` at
    // that moment. Since options for a saved value arrive asynchronously, the
    // initial mapping discards it and never re-runs. Re-key the field once the
    // selection becomes resolvable so the mapping happens against real options.
    selectFieldKey(): string {
      const matchField = this.useAgencyCodeAsValue ? "agencyCode" : "_id";
      const resolvedValues = this.getAgencyField(
        matchField
      ).filter((selectedValue: string) =>
        this.agencies.some(
          (agency: IAgency) => agency[matchField] === selectedValue
        )
      );
      return resolvedValues.sort().join("|");
    },
    hasUnresolvedValues(): boolean {
      const matchField = this.useAgencyCodeAsValue ? "agencyCode" : "_id";
      const selectedValues = this.getAgencyField(matchField);
      return selectedValues.some(
        (selectedValue: string) =>
          !this.agencies.find(
            (agency: IAgency) => agency[matchField] === selectedValue
          )
      );
    },
    agencyListOptions(): any {
      const clonedAgencies = cloneDeep(this.agencies).map((agency: IAgency) => {
        const fieldLabel = this.showOnlyAgencyCodes
          ? agency.agencyCode
          : `${agency.agencyName} - ${agency.agencyCode}`;
        const fieldValue = this.useAgencyCodeAsValue
          ? agency.agencyCode
          : agency._id;
        return {
          label: fieldLabel,
          value: fieldValue,
          key: fieldValue
        };
      });
      // Keep selected agencies at the top of the list; sortBy is stable, so
      // the rest keep their loaded order.
      const selectedValues = this.getAgencyField(
        this.useAgencyCodeAsValue ? "agencyCode" : "_id"
      );
      const sortedAgencies = sortBy(clonedAgencies, ({ value }) =>
        selectedValues.includes(value) ? 0 : 1
      );
      if (this.editing && this.editing._id) {
        const agenciesWithoutCurrentAgency = sortedAgencies.filter(
          ({ key }) => key !== this.editing._id
        );
        return agenciesWithoutCurrentAgency;
      }
      return sortedAgencies;
    }
  }
});
